import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import {
  type SextantErrorKind,
  SextantErrorFigure,
  sextantErrorExplain,
  sextantErrorLabel,
} from "../../ui/SextantErrorFigure";

interface SpotErrorProps {
  onNavigate: (to: string) => void;
}

const MODULE = "astro";
const GAME = "sextant-errors";
const CARDS_PER_SESSION = 10;

const ERROR_KINDS: readonly SextantErrorKind[] = [
  "none",
  "perpendicularity",
  "side",
  "index",
  "collimation",
];

/// Per-card prompt — describes the test scene so the student knows
/// what they're looking at before they diagnose.
function scenePromptFor(kind: SextantErrorKind): string {
  switch (kind) {
    case "none":
    case "side":
    case "index":
      return "You set the arc to 0° and look at the horizon through the eyepiece. What do you see?";
    case "perpendicularity":
      return "You set the arc to about 35° and look at the arc itself through the index mirror.";
    case "collimation":
      return "You take repeated sights of the same body, moving it through the field of view.";
  }
}

interface Card {
  kind: SextantErrorKind;
  scene: string;
  choices: readonly SextantErrorKind[];
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function buildCard(): Card {
  const kind = randChoice(ERROR_KINDS);
  // 4-way multiple choice: the answer plus 3 distractors drawn from
  // the other kinds at random.
  const distractors = ERROR_KINDS.filter((k) => k !== kind);
  for (let i = distractors.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = distractors[i]!;
    distractors[i] = distractors[j]!;
    distractors[j] = a;
  }
  const choices: SextantErrorKind[] = [kind, ...distractors.slice(0, 3)];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }
  return { kind, scene: scenePromptFor(kind), choices };
}

export function SpotError({ onNavigate }: SpotErrorProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<SextantErrorKind | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: SextantErrorKind) => {
      if (chosen || result) return;
      const ok = choice === card.kind;
      const attempt: Omit<Attempt, "module"> = {
        char: card.kind,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/astro")}
      />
    );
  }

  const revealed = chosen !== null;
  const explain = revealed ? sextantErrorExplain(card.kind) : null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "astro" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Spot the sextant error</h3>
        <p style={{ marginBottom: 12 }}>{card.scene}</p>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 12,
            marginBottom: 12,
            background: "var(--bg-elev, #f8fafc)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <SextantErrorFigure kind={card.kind} size={260} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === card.kind;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 15,
                  padding: "12px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {sextantErrorLabel(c)}
              </button>
            );
          })}
        </div>

        {revealed && explain && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.kind
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.kind
                ? `✓ Correct — ${sextantErrorLabel(card.kind)}.`
                : `✗ Not quite — this is a ${sextantErrorLabel(card.kind).toLowerCase()}.`}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              <strong>Cause:</strong> {explain.cause}
            </p>
            <p className="small" style={{ margin: "6px 0 0" }}>
              <strong>Fix:</strong> {explain.fix}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/astro")}>
        ← Back to Astronavigation
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Astronavigation
        </button>
      </div>
    </>
  );
}
